// ABOUTME: Balance component displaying positivity ratio with visual see-saw representation.
// ABOUTME: Shows stars vs triangles balance with special square handling and accessibility support.

import React from 'react';
import { Box, Typography, Paper } from '@mui/material';
import { useTheme } from '@mui/material/styles';
import { Star as StarIcon } from '@mui/icons-material';
import { TriangleIcon } from '../utils/behaviorUtils';
import { decorativeGradients } from '../theme/tokens';

interface BehaviorCounts {
  stars: number;
  triangles: number;
  squares: number;
}

interface PositivityRatioBalanceProps {
  behaviorCounts: BehaviorCounts;
  loading?: boolean;
  compact?: boolean;
}

const PositivityRatioBalance: React.FC<PositivityRatioBalanceProps> = ({
  behaviorCounts,
  loading = false,
  compact = false,
}) => {
  const theme = useTheme();

  // Calculate ratio using same logic as analytics page
  const ratio = behaviorCounts.stars / Math.max(behaviorCounts.triangles + behaviorCounts.squares, 1);
  
  // Determine tilt direction and strength
  let tilt: 'positive' | 'negative' | 'neutral' = 'neutral';
  let tiltStrength: 'light' | 'moderate' | 'strong' = 'light';
  
  if (behaviorCounts.stars === behaviorCounts.triangles) {
    // Equal counts (including 0-0) should be neutral
    tilt = 'neutral';
    tiltStrength = 'light'; // doesn't matter for neutral, but set for consistency
  } else if (ratio > 1.01) {
    tilt = 'positive';
    if (ratio >= 1.5) tiltStrength = 'strong';
    else if (ratio >= 1.25) tiltStrength = 'moderate';
    else tiltStrength = 'light';
  } else if (ratio < 0.99) {
    tilt = 'negative';
    if (ratio <= 0.67) tiltStrength = 'strong';
    else if (ratio <= 0.8) tiltStrength = 'moderate';
    else tiltStrength = 'light';
  } else {
    // ratio between 0.99 and 1.01 = essentially equal
    tilt = 'neutral';
  }

  // Balance status for screen readers
  const getBalanceStatus = () => {
    if (tilt === 'positive') return 'Balance tilted toward positive behavior';
    if (tilt === 'negative') return 'Balance tilted toward negative behavior';
    return 'Balance is neutral';
  };

  // Calculate rotation angle for visual tilt
  // Positive angle = right side (star side) down, left side (triangle side) up
  // Negative angle = left side (triangle side) down, right side (star side) up
  const getRotationAngle = () => {
    // Neutral tilt always returns 0 regardless of strength
    if (tilt === 'neutral') return 0;
    
    if (tiltStrength === 'strong') return tilt === 'positive' ? 15 : -15;
    if (tiltStrength === 'moderate') return tilt === 'positive' ? 8 : -8;
    if (tiltStrength === 'light') return tilt === 'positive' ? 4 : -4;
    return 0;
  };

  const beamWidth = compact ? '55%' : '240px';
  const iconSize = compact ? 16 : 32;
  const countVariant: 'h5' | 'h6' = compact ? 'h6' : 'h5';
  const containerHeight = compact ? '50px' : '120px';
  const beamTop = compact ? '22px' : '40px';
  const fulcrumTop = compact ? '25px' : '44px';
  const sideTop = compact ? '2px' : '25px';
  const fulcrumSize = compact ? 10 : 20;
  const fulcrumHeight = compact ? 14 : 32;

  if (loading) {
    if (compact) {
      return (
        <Box role="status" aria-label="Loading today's balance">
          <Typography variant="body2" color="text.secondary">Loading...</Typography>
        </Box>
      );
    }
    return (
      <Paper
        elevation={2}
        sx={{ p: 3, borderRadius: 2, background: decorativeGradients.lightCard }}
        role="status"
        aria-label="Loading today's balance"
      >
        <Typography variant="h6" gutterBottom sx={{ color: 'text.primary', fontWeight: 600 }}>
          Today&apos;s Balance
        </Typography>
        <Typography variant="body2" color="text.secondary">Loading...</Typography>
      </Paper>
    );
  }

  const content = (
    <>
      {!compact && (
        <Typography
          variant="h6"
          gutterBottom
          sx={{ color: 'text.primary', fontWeight: 600, mb: 2 }}
        >
          Today&apos;s Balance
        </Typography>
      )}

      {/* Balance Visual */}
      <Box
        data-testid="balance-container"
        data-tilt={tilt}
        data-tilt-strength={tiltStrength}
        data-calculated-ratio={ratio.toFixed(1)}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          position: 'relative',
          height: containerHeight,
        }}
      >
        {/* Balance Beam */}
        <Box
          sx={{
            width: beamWidth,
            height: '6px',
            backgroundColor: theme.palette.text.secondary,
            borderRadius: 0.25, // theme.shape.borderRadius(12) * 0.25 = 3px
            transform: `rotate(${getRotationAngle()}deg)`,
            transformOrigin: 'center',
            transition: 'transform 0.5s ease',
            position: 'relative',
            top: beamTop,
          }}
        />

        {/* Fulcrum (center support) */}
        <Box
          sx={{
            width: '0',
            height: '0',
            borderLeft: `${fulcrumSize}px solid transparent`,
            borderRight: `${fulcrumSize}px solid transparent`,
            borderBottom: `${fulcrumHeight}px solid ${theme.palette.text.secondary}`,
            position: 'relative',
            top: fulcrumTop,
          }}
        />

        {/* Triangle Side */}
        <Box
          sx={{
            position: 'absolute',
            left: '5px',
            top: sideTop,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            transform: `translateY(0px)`,
            transition: 'transform 0.5s ease',
          }}
        >
          <TriangleIcon
            data-testid="triangle-icon"
            sx={{
              // Icon glyph sizing via fontSize (16 or 32, matching h6/body1 and h2), not a Typography text override
              fontSize: iconSize,
              color: theme.palette.warning.main,
              mb: compact ? 0.5 : 1,
            }}
            aria-hidden="true"
          />
          <Typography
            data-testid="triangle-count"
            variant={countVariant}
            sx={{
              fontWeight: 600,
              color: theme.palette.warning.main,
              lineHeight: 1,
            }}
            aria-label={`${behaviorCounts.triangles} triangles received today`}
          >
            {behaviorCounts.triangles}
          </Typography>
        </Box>

        {/* Star Side */}
        <Box
          sx={{
            position: 'absolute',
            right: '5px',
            top: sideTop,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            transform: `translateY(0px)`,
            transition: 'transform 0.5s ease',
          }}
        >
          <StarIcon
            data-testid="star-icon"
            sx={{
              // Icon glyph sizing via fontSize (16 or 32, matching h6/body1 and h2), not a Typography text override
              fontSize: iconSize,
              color: theme.palette.success.main,
              mb: compact ? 0.5 : 1,
            }}
            aria-hidden="true"
          />
          <Typography
            data-testid="star-count"
            variant={countVariant}
            sx={{
              fontWeight: 600,
              color: theme.palette.success.main,
              lineHeight: 1,
            }}
            aria-label={`${behaviorCounts.stars} stars earned today`}
          >
            {behaviorCounts.stars}
          </Typography>
        </Box>

      </Box>

      {/* Screen reader status */}
      <Box
        data-testid="balance-status"
        aria-live="polite"
        sx={{
          position: 'absolute',
          left: '-9999px',
          width: '1px',
          height: '1px',
          overflow: 'hidden',
        }}
      >
        {getBalanceStatus()}
      </Box>
    </>
  );

  if (compact) {
    return (
      <Box role="region" aria-label="Today's balance summary" sx={{ width: '100%' }}>
        {content}
      </Box>
    );
  }

  return (
    <Paper
      elevation={2}
      sx={{
        p: 3,
        borderRadius: 2,
        background: decorativeGradients.lightCard,
      }}
      role="region"
      aria-label="Today's balance summary"
    >
      {content}
    </Paper>
  );
};

export default PositivityRatioBalance;